/**
 * @description 把历史商品详情富文本中保存的 Swiper 运行后 DOM 还原为可操作轮播。
 * @param {ParentNode} scope - 富文本根节点或文档节点
 * @returns {Array<() => void>} 窗口尺寸变化时可再次执行的布局刷新函数
 */
export function initRichTextSwipers(scope) {
  if (!scope || typeof scope.querySelectorAll !== 'function') return []

  return findSwiperRoots(scope)
    .map((root) => initSwiper(root))
    .filter(Boolean)
}

/**
 * @description 从富文本根节点中找到 Swiper 根容器，兼容已初始化后的旧 DOM。
 * @param {ParentNode} scope - 富文本根节点或文档节点
 * @returns {Element[]}
 */
function findSwiperRoots(scope) {
  const roots = []
  Array.from(scope.querySelectorAll('.swiper-wrapper')).forEach((wrapper) => {
    const root = wrapper.closest('.swiper,.swiper-initialized') || wrapper.parentElement
    if (root && !roots.includes(root)) {
      roots.push(root)
    }
  })
  return roots
}

/**
 * @description 初始化单个旧 Swiper 容器。
 * @param {Element} root - Swiper 根容器
 * @returns {(() => void) | null}
 */
function initSwiper(root) {
  if (!root || root.dataset.tendaRichTextSwiperEnhanced === '1') return null

  const wrapper = root.querySelector('.swiper-wrapper')
  if (!wrapper) return null

  const slides = Array.from(wrapper.children).filter((child) =>
    child.classList?.contains('swiper-slide')
  )
  if (slides.length < 2) return null

  root.dataset.tendaRichTextSwiperEnhanced = '1'
  const host = findHost(root)
  const pagination =
    root.querySelector('.swiper-pagination') || host?.querySelector('.swiper-pagination')
  const previousButton =
    host?.querySelector('.swiper-button-prev') || root.querySelector('.swiper-button-prev')
  const nextButton =
    host?.querySelector('.swiper-button-next') || root.querySelector('.swiper-button-next')
  const multiSlide = isMultiSlide(root, host, slides.length)
  const state = {
    index: 0,
    maxIndex: slides.length - 1,
    visibleCount: 1,
  }

  host?.classList.add('tenda-rich-text-swiper-host')
  root.classList.add('tenda-rich-text-swiper')
  disableNativeImageDrag(root)
  wrapper.style.removeProperty('transition-duration')
  wrapper.style.removeProperty('transition-delay')
  wrapper.style.removeProperty('transform')
  normalizeRuntimeClasses(root, slides, pagination)
  const bullets = ensurePagination(pagination, slides.length)

  /**
   * @description 根据当前视口重算 slide 宽度和偏移。
   * @returns {void}
   */
  function applyLayout() {
    const gap = multiSlide ? getLegacyGap(slides) : 0
    const visibleCount = getVisibleCount(multiSlide, slides.length)
    const basis =
      visibleCount === 1 ? '100%' : `calc((100% - ${gap * (visibleCount - 1)}px) / ${visibleCount})`

    state.visibleCount = visibleCount
    state.maxIndex = Math.max(0, slides.length - visibleCount)
    state.index = Math.min(state.index, state.maxIndex)
    wrapper.style.gap = `${gap}px`
    slides.forEach((slide) => {
      slide.style.width = ''
      slide.style.marginRight = '0px'
      slide.style.flex = `0 0 ${basis}`
    })
    update()
  }

  /**
   * @description 应用轮播位移、分页和按钮状态。
   * @returns {void}
   */
  function getSlideStep() {
    const firstSlide = slides[0]
    const gap = Number.parseFloat(wrapper.style.gap || '0') || 0
    return firstSlide ? firstSlide.getBoundingClientRect().width + gap : 0
  }

  function getCurrentOffset() {
    return getSlideStep() * state.index
  }

  function translateToOffset(offset) {
    wrapper.style.transform = `translate3d(${-offset}px, 0, 0)`
  }

  function update() {
    translateToOffset(getCurrentOffset())

    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('swiper-slide-active', slideIndex === state.index)
      slide.classList.toggle('swiper-slide-prev', slideIndex === state.index - 1)
      slide.classList.toggle('swiper-slide-next', slideIndex === state.index + 1)
    })
    bullets.forEach((bullet, bulletIndex) => {
      bullet.classList.toggle('swiper-pagination-bullet-active', bulletIndex === state.index)
    })
    previousButton?.classList.toggle('swiper-button-disabled', state.index <= 0)
    nextButton?.classList.toggle('swiper-button-disabled', state.index >= state.maxIndex)
  }

  /**
   * @description 切换轮播索引并限制边界。
   * @param {number} nextIndex - 目标索引
   * @returns {void}
   */
  function setIndex(nextIndex) {
    state.index = Math.max(0, Math.min(state.maxIndex, nextIndex))
    update()
  }

  bindDragGesture({
    getCurrentOffset,
    getSlideStep,
    root,
    setIndex,
    state,
    translateToOffset,
    update,
    wrapper,
  })

  bullets.forEach((bullet, bulletIndex) => {
    bullet.addEventListener('click', () => {
      setIndex(Math.min(bulletIndex, state.maxIndex))
    })
  })
  prepareButton(previousButton, '上一张', () => setIndex(state.index - 1))
  prepareButton(nextButton, '下一张', () => setIndex(state.index + 1))
  applyLayout()

  return applyLayout
}

/**
 * @description 找到轮播外层宿主，用于兼容分页和按钮不在 Swiper 根节点内的旧结构。
 * @param {Element} root - Swiper 根节点
 * @returns {Element}
 */
function findHost(root) {
  return (
    root.closest('.picSliderFromRight,.mySectionSlider,.SectionRow') || root.parentElement || root
  )
}

/**
 * @description 判断类名中是否包含指定片段。
 * @param {Element | null | undefined} node - DOM 节点
 * @param {string} name - 类名片段
 * @returns {boolean}
 */
function hasClassName(node, name) {
  return Boolean(
    node &&
    typeof node.className === 'string' &&
    node.className.toLowerCase().includes(name.toLowerCase())
  )
}

/**
 * @description 判断旧 Swiper 是否为多卡片横滑结构。
 * @param {Element} root - Swiper 根节点
 * @param {Element} host - 外层宿主节点
 * @param {number} slideCount - slide 数量
 * @returns {boolean}
 */
function isMultiSlide(root, host, slideCount) {
  return (
    slideCount > 1 &&
    (hasClassName(root, 'fromright') ||
      hasClassName(host, 'fromright') ||
      hasClassName(root, 'mySwiperFromRight') ||
      hasClassName(host, 'picSliderFromRight'))
  )
}

/**
 * @description 读取旧 Swiper inline 样式里的间距，读取不到时使用旧详情页常见的 30px。
 * @param {Element[]} slides - slide 节点列表
 * @returns {number}
 */
function getLegacyGap(slides) {
  for (const slide of slides) {
    const marginRight = Number.parseFloat(slide.style.marginRight || '')
    if (Number.isFinite(marginRight) && marginRight > 0) {
      return marginRight
    }
  }
  return 30
}

/**
 * @description 计算当前视口下每屏展示数量。
 * @param {boolean} multiSlide - 是否多卡片横滑
 * @param {number} slideCount - slide 数量
 * @returns {number}
 */
function getVisibleCount(multiSlide, slideCount) {
  if (!multiSlide) return 1
  if (window.matchMedia?.('(max-width: 960px)').matches) return 1
  return Math.max(1, Math.min(3, slideCount))
}

/**
 * @description 清理旧 Swiper 运行态 class，避免粘贴后的静态 DOM 停留在不可见位移状态。
 * @param {Element} root - Swiper 根节点
 * @param {Element[]} slides - slide 节点列表
 * @param {Element | null | undefined} pagination - 分页节点
 * @returns {void}
 */
function normalizeRuntimeClasses(root, slides, pagination) {
  root.classList.remove('swiper-initialized', 'swiper-backface-hidden')
  slides.forEach((slide) => {
    slide.classList.remove('swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev')
  })
  if (pagination) {
    Array.from(pagination.children).forEach((bullet) => {
      bullet.classList.remove('swiper-pagination-bullet-active')
    })
  }
}

/**
 * @description Prevents browser-native image dragging from cancelling swiper pointer gestures.
 * @param {Element} root - Swiper root node.
 * @returns {void}
 */
function disableNativeImageDrag(root) {
  root.querySelectorAll('img').forEach((image) => {
    image.setAttribute('draggable', 'false')
  })
  root.addEventListener(
    'dragstart',
    (event) => {
      event.preventDefault?.()
    },
    true
  )
}

/**
 * @description Ensures pagination bullets match the current slide count.
 * @param {Element | null | undefined} pagination - Pagination node.
 * @param {number} slideCount - Slide count.
 * @returns {Element[]}
 */
function ensurePagination(pagination, slideCount) {
  if (!pagination) return []

  pagination.classList.add(
    'swiper-pagination-clickable',
    'swiper-pagination-bullets',
    'swiper-pagination-horizontal'
  )
  if (pagination.children.length !== slideCount) {
    pagination.innerHTML = ''
    for (let index = 0; index < slideCount; index += 1) {
      const bullet = document.createElement('span')
      bullet.className = 'swiper-pagination-bullet'
      pagination.appendChild(bullet)
    }
  }

  return Array.from(pagination.children)
}

/**
 * @description 给旧详情里的 div 按钮补键盘可访问能力并绑定点击。
 * @param {Element | null | undefined} button - 轮播按钮节点
 * @param {string} label - 可访问名称
 * @param {() => void} onClick - 点击回调
 * @returns {void}
 */
function prepareButton(button, label, onClick) {
  if (!button) return

  button.setAttribute('role', 'button')
  button.setAttribute('tabindex', '0')
  button.setAttribute('aria-label', label)
  button.addEventListener('click', onClick)
  button.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick()
    }
  })
}

/**
 * @description Adds horizontal drag support to legacy rich-text swiper markup.
 * @param {object} options - Drag binding options.
 * @param {Element} options.root - Swiper root node.
 * @param {Element} options.wrapper - Swiper wrapper node.
 * @param {{ index: number, maxIndex: number }} options.state - Current swiper state.
 * @param {() => number} options.getCurrentOffset - Reads the current translate offset.
 * @param {() => number} options.getSlideStep - Reads the width of one slide step.
 * @param {(offset: number) => void} options.translateToOffset - Applies a live drag offset.
 * @param {(nextIndex: number) => void} options.setIndex - Commits the target slide index.
 * @param {() => void} options.update - Restores the committed visual state.
 * @returns {void}
 */
function bindDragGesture({
  root,
  wrapper,
  state,
  getCurrentOffset,
  getSlideStep,
  translateToOffset,
  setIndex,
  update,
}) {
  const runtimeWindow = root.ownerDocument?.defaultView || globalThis.window
  let dragState = null
  let suppressClick = false

  function getGesturePoint(event) {
    const point = event.touches?.[0] || event.changedTouches?.[0] || event
    if (typeof point?.clientX !== 'number' || typeof point?.clientY !== 'number') {
      return null
    }
    return { x: point.clientX, y: point.clientY }
  }

  function startGesture(event) {
    if (event.button !== undefined && event.button !== 0) return

    const point = getGesturePoint(event)
    if (!point) return

    dragState = {
      baseOffset: getCurrentOffset(),
      dragging: false,
      lastX: point.x,
      lastY: point.y,
      restoreTransition: wrapper.style.transition || '',
      slideStep: getSlideStep(),
      startX: point.x,
      startY: point.y,
    }
    root.setPointerCapture?.(event.pointerId)
  }

  function moveGesture(event) {
    if (!dragState) return

    const point = getGesturePoint(event)
    if (!point) return

    const deltaX = point.x - dragState.startX
    const deltaY = point.y - dragState.startY
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)

    if (!dragState.dragging) {
      if (absX < 8 && absY < 8) return
      if (absY > absX) {
        dragState = null
        return
      }
      dragState.dragging = true
      wrapper.style.transition = 'none'
      root.classList.add('tenda-rich-text-swiper--dragging')
    }

    if (event.cancelable !== false) {
      event.preventDefault?.()
    }

    dragState.lastX = point.x
    dragState.lastY = point.y

    const maxOffset = dragState.slideStep * state.maxIndex
    let nextOffset = dragState.baseOffset - deltaX
    if (nextOffset < 0) {
      nextOffset /= 3
    } else if (nextOffset > maxOffset) {
      nextOffset = maxOffset + (nextOffset - maxOffset) / 3
    }
    translateToOffset(nextOffset)
  }

  function clearDragVisuals() {
    wrapper.style.transition = dragState?.restoreTransition || ''
    root.classList.remove('tenda-rich-text-swiper--dragging')
  }

  function finishGesture(event) {
    if (!dragState) return

    const point = getGesturePoint(event) || { x: dragState.lastX, y: dragState.lastY }
    const deltaX = point.x - dragState.startX
    const wasDragging = dragState.dragging
    const threshold = Math.max(40, dragState.slideStep * 0.15)

    clearDragVisuals()
    dragState = null

    if (wasDragging && Math.abs(deltaX) >= threshold) {
      setIndex(state.index + (deltaX < 0 ? 1 : -1))
    } else {
      update()
    }

    if (wasDragging) {
      suppressClick = true
      const defer = runtimeWindow?.setTimeout || globalThis.setTimeout
      if (typeof defer === 'function') {
        defer(() => {
          suppressClick = false
        }, 0)
      } else {
        suppressClick = false
      }
    }
  }

  function cancelGesture() {
    if (!dragState) return
    clearDragVisuals()
    dragState = null
    update()
  }

  function suppressDraggedClick(event) {
    if (!suppressClick) return
    event.preventDefault?.()
    event.stopPropagation?.()
    suppressClick = false
  }

  if (runtimeWindow?.PointerEvent) {
    root.addEventListener('pointerdown', startGesture)
    root.addEventListener('pointermove', moveGesture)
    root.addEventListener('pointerup', finishGesture)
    root.addEventListener('pointercancel', cancelGesture)
  } else {
    root.addEventListener('touchstart', startGesture, { passive: true })
    root.addEventListener('touchmove', moveGesture, { passive: false })
    root.addEventListener('touchend', finishGesture)
    root.addEventListener('touchcancel', cancelGesture)
  }
  root.addEventListener('click', suppressDraggedClick, true)
}
